import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit
} from '@angular/core';
import { DialogWrapperComponent } from '../../../../../shared/components/wrappers/dialog-wrapper/dialog-wrapper.component';
import {
  RadioCardComponent,
  RadioCardSubitem
} from '../../../../../shared/components/controllers/radio-card/radio-card.component';
import {
  DELIVERY_HAND_OPTIONS,
  DELIVERY_LEAVE_OPTIONS
} from '../../../../../shared/constants/dropoff-options.constant';
import { TextareaComponent } from '../../../../../shared/components/controllers/textarea/textarea.component';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { ButtonComponent } from '../../../../../shared/components/button/button.component';
import {
  ButtonSize,
  ButtonStyle
} from '../../../../../shared/enums/button.enum';
import { BaseDialogDirective } from '../../../../../shared/components/base/base-dialog.directive';
import { AptSuiteDialogComponent } from '../../../../../shared/components/dialogs/apt-suite-dialog/apt-suite-dialog.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { take } from 'rxjs';
import { DynamicValidationDependencies } from '../../../../../shared/components/controllers/control-error/validation-error-messages.token';

export interface CheckoutDropoffDialogResponse {
  delivery: RadioCardSubitem | undefined;
  instructions: string;
  apt_suite: string;
}

@UntilDestroy()
@Component({
  selector: 'app-checkout-dropoff-dialog',
  imports: [
    DialogWrapperComponent,
    RadioCardComponent,
    TextareaComponent,
    ReactiveFormsModule,
    ButtonComponent,
    ...DynamicValidationDependencies
  ],
  templateUrl: './checkout-dropoff-dialog.component.html',
  styleUrl: './checkout-dropoff-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CheckoutDropoffDialogComponent
  extends BaseDialogDirective<CheckoutDropoffDialogResponse>
  implements OnInit
{
  protected readonly ButtonStyle = ButtonStyle;
  protected readonly ButtonSize = ButtonSize;
  protected readonly DELIVERY_HAND_OPTIONS: RadioCardSubitem[] =
    DELIVERY_HAND_OPTIONS;
  protected readonly DELIVERY_LEAVE_OPTIONS: RadioCardSubitem[] =
    DELIVERY_LEAVE_OPTIONS;

  dropoffForm!: FormGroup;

  constructor(
    private formBuilder: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit() {
    this.initDropoffForm();
  }

  onConfirmInstructions() {
    if (this.dropoffForm.invalid) {
      this.dropoffForm.markAllAsTouched();
      return;
    }

    const formValues = this.dropoffForm.value;

    const selectedDeliveryOption = [
      ...this.DELIVERY_HAND_OPTIONS,
      ...this.DELIVERY_LEAVE_OPTIONS
    ].find((option) => option.value === formValues.delivery);

    this.closeDialog({ ...formValues, delivery: selectedDeliveryOption });
  }

  openAptSuiteDialog(
    apt_suite: string = this.dropoffForm.get('apt_suite')!.value
  ) {
    const dialogRef = this.dialog.open(AptSuiteDialogComponent, {
      data: { title: 'Add Apt/Suite', aptSuite: apt_suite },
      width: '100%',
      maxWidth: '550px'
    });

    dialogRef.closed.pipe(take(1), untilDestroyed(this)).subscribe({
      next: (apt_suite: unknown) => {
        this.dropoffForm.patchValue({ apt_suite });
        this.cdr.markForCheck();
      }
    });
  }

  private initDropoffForm(
    dropoff: CheckoutDropoffDialogResponse = this.dialogData['dropoff']
  ): void {
    this.dropoffForm = this.formBuilder.group({
      delivery: [dropoff?.delivery?.value || '', [Validators.required]],
      instructions: dropoff?.instructions || '',
      apt_suite: dropoff?.apt_suite || ''
    });
  }
}
